/* CoE Compliance Queue — iter98.99dx Phase B
 *
 * Expanded from Phase 2 (basic queue table) with:
 *   - Filter chips: Week 1-4 / 5-12 / 13+ / Low compliance / Check-in due /
 *     Has escalation. Applied on the engageable tab.
 *   - Per-row wear-time sparkline (7 bars, tone-coded) + 7d % pill
 *   - Per-row sentiment dot (positive/neutral/frustrated) derived from
 *     the last 5 patient messages by simple keyword classifier (backend).
 *   - Per-row "Check in now" rose button that opens CheckInNowModal —
 *     1-click templated outreach without opening the deep case page.
 *   - Open-escalation badge on rows that need clinical follow-through.
 *
 * Data feed: GET /coe/compliance/my-queue (now enriched in iter98.99dx
 * with wear_hours_7d, wear_pct_7d, treatment_week, sentiment,
 * days_since_check_in, has_open_escalation, thread_id).
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
    Heart, Stethoscope, Sparkle, Clock, ArrowsClockwise, ArrowsDownUp,
    Warning, ChatCircleDots, ClockUser, UsersThree,
} from "@phosphor-icons/react";
import CheckInNowModal from "@/components/coe/CheckInNowModal";
import LogWearForPatientModal from "@/components/coe/LogWearForPatientModal";
import { WearSparkline, SentimentDot } from "@/components/coe/ComplianceSignals";
import { AdminKpiTile, CommandHeader } from "@/components/admin/AdminKpiTile";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
// iter102.27 — Sort dropdown uses the exact doctor-portal grammar
// (DropdownMenu + ArrowsDownUp icon + `Sort by` label + radio group).
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuLabel, DropdownMenuSeparator,
    DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
// iter102.25 — Killed the local STATUS_TONE map; use the canonical
// `statusLabel` + `statusToneClass` so the queue reads the same words
// the doctor + detail page use for every status.
import { statusLabel, statusToneClass } from "@/lib/caseStatus";


function relativeTimeFromIso(iso) {
    if (!iso) return "—";
    try {
        const then = new Date(iso).getTime();
        const days = Math.max(0, Math.floor((Date.now() - then) / 86400000));
        if (days === 0) return "today";
        if (days === 1) return "1 day ago";
        if (days < 30) return `${days} days ago`;
        const months = Math.floor(days / 30);
        return months === 1 ? "1 month ago" : `${months} months ago`;
    } catch {
        return "—";
    }
}

/* iter102.25 — Compact days-in-current-stage helper for the
   pre-engagement column. Returns e.g. "6d" or "today". */
function daysInStageFromIso(iso) {
    if (!iso) return "—";
    try {
        const then = new Date(iso).getTime();
        const days = Math.max(0, Math.floor((Date.now() - then) / 86400000));
        if (days === 0) return "today";
        return `${days}d`;
    } catch {
        return "—";
    }
}

/* iter102.25 — Short, chip-friendly date formatter for the "Created"
   column on the pre-engagement tab (e.g. "10 Aug"). */
function shortDateFromIso(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch {
        return "—";
    }
}


// Filter chip catalog for the ENGAGEABLE tab (patient wearing aligners).
// `predicate` runs against each enriched row; the active chip narrows the
// list on the engageable tab.
const FILTERS = [
    { id: "all",        label: "All",            predicate: () => true },
    { id: "week_1_4",   label: "Week 1-4",       predicate: (r) => r.treatment_week != null && r.treatment_week < 4 },
    { id: "week_5_12",  label: "Week 5-12",      predicate: (r) => r.treatment_week != null && r.treatment_week >= 4 && r.treatment_week < 12 },
    { id: "week_13",    label: "Week 13+",       predicate: (r) => r.treatment_week != null && r.treatment_week >= 12 },
    { id: "low_wear",   label: "Low compliance", predicate: (r) => (r.wear_pct_7d ?? 100) < 70 },
    { id: "check_in",   label: "Check-in due",   predicate: (r) => r.days_since_check_in == null || r.days_since_check_in >= 1 },
    { id: "escalation", label: "Has escalation", predicate: (r) => r.has_open_escalation },
];

// iter102.26 — Filter chip catalog for the PRE-ENGAGEMENT tab (case still
// pre-fitment). Signals here are lifecycle-shaped, not wear-shaped, so
// this catalog is intentionally separate. `stuck` = 7d+ in the same status;
// `awaiting_approval` = plan still with the doctor; `ready_soon` = case
// is heading straight into fitment on the next milestone.
const PRE_FILTERS = [
    { id: "all",           label: "All",              predicate: () => true },
    { id: "stuck",         label: "Stuck",            predicate: (r) => {
        if (!r.status_updated_at) return false;
        const days = Math.floor((Date.now() - new Date(r.status_updated_at).getTime()) / 86400000);
        return days >= 7;
    } },
    { id: "awaiting_approval", label: "Awaiting approval", predicate: (r) => ["submitted", "sent_for_approval"].includes(r.status) },
    { id: "in_production", label: "In production",    predicate: (r) => ["in_production", "assigned_to_production", "submitted_to_production", "approved"].includes(r.status) },
    { id: "ready_soon",    label: "Ready soon",       predicate: (r) => ["shipped", "delivered"].includes(r.status) },
];


export default function CoeComplianceQueue() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("engageable");
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    // iter102.27 — Sort state. Two sort catalogs (engageable vs pre)
    // because the ordering that helps a coach differs between the two
    // lifecycle halves. Defaults surface the "needs attention" bucket
    // at the top (worst-wear first on engageable, longest-stuck first
    // on pre-engagement).
    const [sortBy, setSortBy] = useState("wear_asc");
    const [checkInCase, setCheckInCase] = useState(null);
    const [logWearCase, setLogWearCase] = useState(null);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/coe/compliance/my-queue");
            setRows(r.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const { engageable, preEngagement } = useMemo(() => {
        const eng = [];
        const pre = [];
        for (const r of rows) {
            if (r.engageable) eng.push(r);
            else pre.push(r);
        }
        return { engageable: eng, preEngagement: pre };
    }, [rows]);

    // Pipeline: tab → filter chip → search box → sort.
    // iter102.28 — Third tab `"all"` (triggered by the Total assigned KPI
    // click) unions both lifecycle halves and renders through the
    // pre-engagement column grammar (stage/in-stage/created work for
    // every case regardless of lifecycle phase).
    const baseList = tab === "engageable" ? engageable : (tab === "pre" ? preEngagement : rows);
    // iter102.26 → 102.28 — Each tab has its own filter catalog. The
    // `all` view reuses the pre-catalog (lifecycle-shaped signals) since
    // that grammar applies to every case.
    const activeFilters = tab === "engageable" ? FILTERS : PRE_FILTERS;
    const filterFn = activeFilters.find((f) => f.id === filter)?.predicate || (() => true);
    const filtered = baseList.filter(filterFn);
    const searched = filtered.filter((r) => {
        if (!query.trim()) return true;
        const q = query.trim().toLowerCase();
        return (
            (r.patient_name || "").toLowerCase().includes(q) ||
            (r.code || "").toLowerCase().includes(q) ||
            (r.doctor_name || "").toLowerCase().includes(q)
        );
    });

    // iter102.27 — Sort catalog (tab-aware). Defaults keep the "who
    // needs help" bucket up top: engageable → worst wear first; pre-
    // engagement → longest stuck first.
    const engageableSortOptions = [
        { id: "wear_asc",     label: "Lowest wear first",        cmp: (a, b) => (a.wear_pct_7d ?? 100) - (b.wear_pct_7d ?? 100) },
        { id: "recent_activity", label: "Recent activity",       cmp: (a, b) => new Date(b.status_updated_at || b.created_at).getTime() - new Date(a.status_updated_at || a.created_at).getTime() },
        { id: "patient_az",   label: "Patient A→Z",              cmp: (a, b) => (a.patient_name || "").localeCompare(b.patient_name || "") },
        { id: "doctor_az",    label: "Doctor A→Z",               cmp: (a, b) => (a.doctor_name || "").localeCompare(b.doctor_name || "") },
    ];
    const preSortOptions = [
        { id: "stage_desc",   label: "Longest in stage first",   cmp: (a, b) => new Date(a.status_updated_at || 0).getTime() - new Date(b.status_updated_at || 0).getTime() },
        { id: "created_desc", label: "Newest first",             cmp: (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime() },
        { id: "patient_az",   label: "Patient A→Z",              cmp: (a, b) => (a.patient_name || "").localeCompare(b.patient_name || "") },
        { id: "doctor_az",    label: "Doctor A→Z",               cmp: (a, b) => (a.doctor_name || "").localeCompare(b.doctor_name || "") },
    ];
    const activeSortOptions = tab === "engageable" ? engageableSortOptions : preSortOptions;
    const sortCmp = activeSortOptions.find((o) => o.id === sortBy)?.cmp;
    const visible = sortCmp ? [...searched].sort(sortCmp) : searched;
    const sortLabel = activeSortOptions.find((o) => o.id === sortBy)?.label || "Sort";

    // Counts for filter chip badges (per-tab).
    const filterCounts = useMemo(() => {
        const out = {};
        for (const f of FILTERS) {
            out[f.id] = engageable.filter(f.predicate).length;
        }
        return out;
    }, [engageable]);
    const preFilterCounts = useMemo(() => {
        // iter102.28 — When the "Total assigned" (all) tab is active, the
        // pre-filter chips should count across every row so the totals
        // match what's actually in view. On the pre-only tab they stay
        // scoped to the pre-engagement subset.
        const scope = tab === "all" ? rows : preEngagement;
        const out = {};
        for (const f of PRE_FILTERS) {
            out[f.id] = scope.filter(f.predicate).length;
        }
        return out;
    }, [preEngagement, rows, tab]);

    return (
        <div className="space-y-6" data-testid="coe-compliance-queue">
            <CommandHeader
                overline="Patient Engagement · Compliance"
                title="Compliance Monitoring & Support"
                subtitle="Patients whose doctor opted-in for our team to manage post-fitment compliance. Coach on wear-time, answer day-to-day questions, escalate anything clinical."
                testid="coe-compliance-header"
                right={
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-sm hover:bg-secondary/40 transition"
                        data-testid="coe-compliance-refresh"
                    >
                        <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                }
            />

            {/* iter101.40 → 102.26 — Tab-aware KPI strip.
                Three lifecycle tiles on the left are constant (Engageable
                now / Pre-engagement / Total assigned) and double as tab
                switchers. The three tiles on the right swap their metric
                set based on the active tab so every tile stays actionable:
                  · Engageable tab → wear/engagement signals
                    (Low compliance · Check-in due · Has escalation).
                  · Pre-engagement tab → lifecycle-shaped signals
                    (Stuck · Awaiting approval · Ready soon). */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="coe-compliance-kpi-strip">
                <AdminKpiTile label="Total assigned"  value={rows.length}                             tone="primary" Icon={UsersThree}   active={tab === "all"}        onClick={() => { setTab("all"); setFilter("all"); setSortBy("stage_desc"); }}     testid="coe-compliance-kpi-total" />
                <AdminKpiTile label="Engageable now"  value={engageable.length}                       tone="emerald" Icon={Heart}         active={tab === "engageable"} onClick={() => { setTab("engageable"); setFilter("all"); setSortBy("wear_asc"); }} testid="coe-compliance-kpi-engageable" />
                <AdminKpiTile label="Pre-engagement"  value={preEngagement.length}                    tone="violet"  Icon={Stethoscope}   active={tab === "pre"}        onClick={() => { setTab("pre"); setFilter("all"); setSortBy("stage_desc"); }}     testid="coe-compliance-kpi-pre" />
                {tab === "engageable" ? (
                    <>
                        <AdminKpiTile label="Low compliance"  value={filterCounts.low_wear ?? 0}              tone="rose"    Icon={Warning}       active={filter === "low_wear"}   onClick={() => setFilter("low_wear")}   testid="coe-compliance-kpi-low-wear" />
                        <AdminKpiTile label="Check-in due"    value={filterCounts.check_in ?? 0}              tone="amber"   Icon={ClockUser}     active={filter === "check_in"}   onClick={() => setFilter("check_in")}   testid="coe-compliance-kpi-check-in" />
                        <AdminKpiTile label="Has escalation"  value={filterCounts.escalation ?? 0}            tone="rose"    Icon={ChatCircleDots} active={filter === "escalation"} onClick={() => setFilter("escalation")} testid="coe-compliance-kpi-escalation" />
                    </>
                ) : (
                    <>
                        <AdminKpiTile label="Stuck"              value={preFilterCounts.stuck ?? 0}              tone="rose"    Icon={Warning}     active={filter === "stuck"}              onClick={() => setFilter("stuck")}              testid="coe-compliance-kpi-stuck" />
                        <AdminKpiTile label="Awaiting approval"  value={preFilterCounts.awaiting_approval ?? 0}  tone="amber"   Icon={ClockUser}   active={filter === "awaiting_approval"}  onClick={() => setFilter("awaiting_approval")}  testid="coe-compliance-kpi-awaiting-approval" />
                        <AdminKpiTile label="Ready soon"         value={preFilterCounts.ready_soon ?? 0}         tone="emerald" Icon={Sparkle}     active={filter === "ready_soon"}         onClick={() => setFilter("ready_soon")}         testid="coe-compliance-kpi-ready-soon" />
                    </>
                )}
            </div>

            {/* iter102.27 — Removed the redundant AdminSegmentedTabs.
                The "Engageable now" + "Pre-engagement" KPI tiles above
                already act as tab switchers, so a second segmented
                control was pure duplication.

                Search bar now matches the doctor-portal grammar exactly
                (see /pages/doctor/CasesPipeline.jsx): FilterSearchBar +
                inline sort DropdownMenu in the rightSlot, followed by
                the FilterChipRow below. */}
            <FilterSearchBar
                query={query}
                setQuery={setQuery}
                placeholder="Search patient, doctor, case code…"
                searchTestid="coe-compliance-search"
                testid="coe-compliance-search-bar"
                rightSlot={
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
                                title="Sort queue"
                                data-testid="coe-compliance-sort-btn"
                            >
                                <ArrowsDownUp size={12} /> {sortLabel}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[210px]">
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Sort by</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
                                {activeSortOptions.map((opt) => (
                                    <DropdownMenuRadioItem
                                        key={opt.id}
                                        value={opt.id}
                                        className="text-xs"
                                        data-testid={`coe-compliance-sort-${opt.id}`}
                                    >
                                        {opt.label}
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                }
            />

            {/* iter101.40 → 102.26 — Filter chip row (tab-aware).
                Engageable tab uses the wear/engagement catalog; pre-
                engagement tab uses the lifecycle catalog defined at
                the top of the file. */}
            {tab === "engageable" ? (
                <FilterChipRow
                    chips={FILTERS.map((f) => ({
                        id: f.id,
                        label: f.label,
                        count: filterCounts[f.id] ?? 0,
                        tone: f.id === "escalation" || f.id === "low_wear" ? "rose" : (f.id === "check_in" ? "amber" : "primary"),
                    }))}
                    value={filter}
                    onChange={setFilter}
                    testid="coe-compliance-filter-chips"
                    chipTestidPrefix="coe-compliance-filter"
                />
            ) : (
                <FilterChipRow
                    chips={PRE_FILTERS.map((f) => ({
                        id: f.id,
                        label: f.label,
                        count: preFilterCounts[f.id] ?? 0,
                        tone: f.id === "stuck" ? "rose" : (f.id === "awaiting_approval" ? "amber" : (f.id === "ready_soon" ? "emerald" : "primary")),
                    }))}
                    value={filter}
                    onChange={setFilter}
                    testid="coe-compliance-pre-filter-chips"
                    chipTestidPrefix="coe-compliance-pre-filter"
                />
            )}

            {/* Table */}
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-sm text-muted-foreground">Loading your queue…</div>
                ) : visible.length === 0 ? (
                    <EmptyState
                        title={tab === "engageable" ? "No patients in treatment match" : "No pre-engagement cases"}
                        helper={
                            rows.length === 0
                                ? "Admin hasn't assigned any compliance cases to you yet. Once a doctor opts-in for CoE-Support and admin assigns you, the case lands here."
                                : (filter !== "all"
                                    ? `No engageable patients match the "${FILTERS.find((f) => f.id === filter)?.label}" filter. Try a different chip.`
                                    : (tab === "engageable"
                                        ? "Your assigned patients aren't in treatment yet. Check the Pre-engagement tab to read up on cases before fitment."
                                        : "All your assigned patients are already in treatment."))
                        }
                    />
                ) : (
                    <div className="overflow-x-auto">
                        {/* iter98.99eg — Locked column widths via table-fixed +
                            percentages so every row aligns.
                            iter102.25 — Column set is now tab-aware:
                             · Engageable tab (patient wearing aligners):
                               PATIENT · DOCTOR · 7D WEAR · SIGNALS · LAST
                               ACTIVITY · ACTION
                             · Pre-engagement tab (case pre-fitment, no
                               wear/thread yet): PATIENT · DOCTOR · STAGE ·
                               IN STAGE · CREATED
                             — the pre-engagement row used to render three
                             columns of dead space (empty Signals, empty
                             Action, wear cell fallback to raw status).
                             Now every cell carries actionable info for
                             the coach: where in the lifecycle the case is,
                             how long it's been stuck there, and when it
                             was opened. */}
                        <table className="w-full table-fixed text-sm">
                            <thead className="bg-secondary/30 border-b border-border">
                                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                                    {tab === "engageable" ? (
                                        <>
                                            <th className="px-5 py-3 w-[26%]">Patient</th>
                                            <th className="px-3 py-3 w-[17%]">Doctor</th>
                                            <th className="px-3 py-3 w-[16%]">7d wear</th>
                                            <th className="px-3 py-3 w-[14%]">Signals</th>
                                            <th className="px-3 py-3 w-[11%] text-right whitespace-nowrap">Last activity</th>
                                            <th className="px-3 py-3 w-[16%] text-right">Action</th>
                                        </>
                                    ) : (
                                        <>
                                            <th className="px-5 py-3 w-[30%]">Patient</th>
                                            <th className="px-3 py-3 w-[22%]">Doctor</th>
                                            <th className="px-3 py-3 w-[18%]">Stage</th>
                                            <th className="px-3 py-3 w-[15%] text-right whitespace-nowrap" title="How long the case has been in its current stage">In stage</th>
                                            <th className="px-3 py-3 w-[15%] text-right whitespace-nowrap">Created</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((r) => (
                                    <ComplianceRow
                                        key={r.id}
                                        row={r}
                                        onOpen={() => navigate(`/coe/compliance/cases/${r.id}`)}
                                        onCheckIn={() => setCheckInCase(r)}
                                        onLogWear={() => setLogWearCase(r)}
                                        isEngageable={tab === "engageable"}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Check-in modal — controlled by row button clicks */}
            <CheckInNowModal
                open={!!checkInCase}
                caseRow={checkInCase}
                onClose={() => setCheckInCase(null)}
                onSent={load}
            />

            {/* Log-wear-for-patient modal — iter98.99dy */}
            <LogWearForPatientModal
                open={!!logWearCase}
                caseRow={logWearCase}
                onClose={() => setLogWearCase(null)}
                onSaved={load}
            />
        </div>
    );
}


function ComplianceRow({ row, onOpen, onCheckIn, onLogWear, isEngageable }) {
    // iter98.99eg — Fixed-width grid + align-middle so every cell sits on
    // the same baseline regardless of how many signal pills a row has.
    // iter102.25 — Split the render into two column sets (engageable vs
    // pre-engagement) so each tab shows only the fields that carry
    // information for that lifecycle stage. Patient + Doctor cells are
    // shared between the two.
    const PatientCell = (
        <td className="px-5 py-3 align-middle">
            <div className="flex items-center gap-2.5 min-w-0">
                <Avatar name={row.patient_name} image={row.patient_image} />
                <div className="min-w-0">
                    <div className="font-medium truncate">{row.patient_name || "Unnamed patient"}</div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">
                        {row.code}
                        {row.treatment_week != null && (
                            <span className="ml-2 text-foreground/70 font-sans">· Week {row.treatment_week + 1}</span>
                        )}
                    </div>
                </div>
            </div>
        </td>
    );
    const DoctorCell = (
        <td className="px-3 py-3 align-middle">
            <div className="flex items-center gap-2 min-w-0">
                <Stethoscope size={13} className="text-muted-foreground shrink-0" />
                <div className="min-w-0">
                    <div className="truncate text-[13px]">{row.doctor_name || "—"}</div>
                    {row.doctor_clinic && (
                        <div className="text-[11px] text-muted-foreground truncate">{row.doctor_clinic}</div>
                    )}
                </div>
            </div>
        </td>
    );

    return (
        <tr
            className="border-b border-border last:border-0 hover:bg-secondary/20 transition cursor-pointer"
            data-testid={`coe-compliance-row-${row.code}`}
            onClick={onOpen}
        >
            {PatientCell}
            {DoctorCell}
            {isEngageable ? (
                <>
                    <td className="px-3 py-3 align-middle">
                        {row.wear_hours_7d ? (
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <WearSparkline hours7d={row.wear_hours_7d} testid={`wear-spark-${row.code}`} />
                                    <WearPct pct={row.wear_pct_7d} />
                                </div>
                                {/* iter102.27 — Engageable next-milestone
                                    hint (next tray change / treatment ends).
                                    Sits directly under the wear sparkline
                                    because it's the same clinical rhythm
                                    the coach is coaching against. */}
                                {row.next_milestone && (
                                    <div
                                        className="text-[10.5px] text-muted-foreground inline-flex items-center gap-1 whitespace-nowrap"
                                        data-testid={`coe-engage-milestone-${row.code}`}
                                        title={row.next_milestone.eta_days != null
                                            ? `${row.next_milestone.label} in about ${row.next_milestone.eta_days} days`
                                            : `${row.next_milestone.label} — due soon`}
                                    >
                                        <Sparkle size={9} weight="fill" className="text-primary/70 shrink-0" />
                                        <span className="font-medium">{row.next_milestone.label}</span>
                                        {row.next_milestone.eta_days != null ? (
                                            <span className="text-primary/80 font-medium">
                                                {row.next_milestone.eta_days <= 1 ? "· ~1d" : `· ~${row.next_milestone.eta_days}d`}
                                            </span>
                                        ) : (
                                            <span className="text-rose-600 font-medium">· due</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                    </td>
                    <td className="px-3 py-3 align-middle">
                        <div className="flex flex-wrap items-center gap-1.5">
                            {row.has_open_escalation && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700" data-testid={`escalation-pill-${row.code}`}>
                                    <Warning size={10} weight="fill" /> Escalation
                                </span>
                            )}
                            <SentimentDot value={row.sentiment} testid={`sentiment-pill-${row.code}`} />
                            {row.days_since_check_in != null && row.days_since_check_in >= 1 && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
                                    <ChatCircleDots size={10} weight="duotone" /> {row.days_since_check_in}d quiet
                                </span>
                            )}
                        </div>
                    </td>
                    <td className="px-3 py-3 align-middle text-right text-muted-foreground text-[11.5px] whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                            <Clock size={11} />
                            {relativeTimeFromIso(row.status_updated_at || row.created_at)}
                        </span>
                    </td>
                    <td className="px-3 py-3 align-middle text-right">
                        {/* iter98.99eg — Replaced the tiny icon-only log-wear button
                            with a proper pill so the action is discoverable. The two
                            pills stack vertically (flex-col, w-[110px]) so the column
                            stays compact AND each action gets a full label. */}
                        <div className="inline-flex flex-col items-stretch gap-1.5 w-[110px] ml-auto">
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onLogWear(); }}
                                className="inline-flex items-center justify-center gap-1.5 h-7 px-2.5 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-800 hover:text-amber-900 text-[11px] font-semibold transition-colors whitespace-nowrap border border-amber-300"
                                data-testid={`coe-logwear-btn-${row.code}`}
                                title="Log wear-time on behalf of the patient (after phone call, in-app help, or in-person visit)"
                            >
                                <ClockUser size={11} weight="bold" /> Log wear
                            </button>
                            {row.thread_id && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onCheckIn(); }}
                                    className="inline-flex items-center justify-center gap-1.5 h-7 px-2.5 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-800 hover:text-rose-900 text-[11px] font-semibold transition-colors whitespace-nowrap border border-rose-300"
                                    data-testid={`coe-checkin-btn-${row.code}`}
                                    title="Send a warm WhatsApp nudge — templated so it goes out in one tap"
                                >
                                    <Heart size={11} weight="fill" /> Check in
                                </button>
                            )}
                        </div>
                    </td>
                </>
            ) : (
                <>
                    <td className="px-3 py-3 align-middle" data-testid={`coe-stage-${row.code}`}>
                        <div className="space-y-1">
                            <StatusBadge status={row.status} />
                            {/* iter102.26 — Next-milestone hint tells the
                                coach where the case is heading + how long
                                until it becomes engageable. Backend emits
                                `next_milestone.eta_days` when a positive
                                remaining window exists; when it's null,
                                we still surface the milestone label but
                                without an ETA. */}
                            {row.next_milestone && (
                                <div
                                    className="text-[10.5px] text-muted-foreground inline-flex items-center gap-1"
                                    data-testid={`coe-next-milestone-${row.code}`}
                                    title={row.next_milestone.eta_days != null
                                        ? `Next expected milestone: ${row.next_milestone.label} in about ${row.next_milestone.eta_days} days`
                                        : `Next expected milestone: ${row.next_milestone.label}`}
                                >
                                    <Sparkle size={9} weight="fill" className="text-primary/70" />
                                    <span className="font-medium">Next:</span>
                                    <span>{row.next_milestone.label}</span>
                                    {row.next_milestone.eta_days != null && (
                                        <span className="text-primary/80 font-medium">
                                            {row.next_milestone.eta_days <= 1 ? "· ~1 day" : `· ~${row.next_milestone.eta_days} days`}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </td>
                    <td className="px-3 py-3 align-middle text-right text-muted-foreground text-[11.5px] whitespace-nowrap" data-testid={`coe-in-stage-${row.code}`}>
                        <span className="inline-flex items-center gap-1" title="How long the case has been in its current stage">
                            <Clock size={11} />
                            {daysInStageFromIso(row.status_updated_at)}
                        </span>
                    </td>
                    <td className="px-3 py-3 align-middle text-right text-muted-foreground text-[11.5px] whitespace-nowrap" data-testid={`coe-created-${row.code}`}>
                        {shortDateFromIso(row.created_at)}
                    </td>
                </>
            )}
        </tr>
    );
}


function WearPct({ pct }) {
    if (pct == null) return null;
    const tone = pct >= 80
        ? "text-emerald-700"
        : pct >= 60
            ? "text-amber-700"
            : "text-rose-700";
    return (
        <span className={`font-display text-[12px] font-bold tabular-nums ${tone}`}>
            {pct}%
        </span>
    );
}


function SummaryTile({ label, value, tone = "primary", helper, testId, active = false, onClick }) {
    const palette = {
        primary:  { bg: "bg-primary/5", border: "border-primary/20", num: "text-primary", ring: "ring-primary/40" },
        emerald:  { bg: "bg-emerald-50", border: "border-emerald-200", num: "text-emerald-700", ring: "ring-emerald-400" },
        violet:   { bg: "bg-violet-50", border: "border-violet-200", num: "text-violet-700", ring: "ring-violet-400" },
    }[tone] || { bg: "bg-card", border: "border-border", num: "text-foreground", ring: "ring-primary/40" };
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`rounded-2xl ${palette.bg} border ${palette.border} px-5 py-4 text-left transition-all ${
                onClick ? "hover:shadow-md cursor-pointer" : ""
            } ${active ? `ring-2 ${palette.ring} shadow-sm` : ""}`}
            data-testid={testId}
            aria-pressed={active}
        >
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
            <div className={`font-display text-3xl font-bold mt-1 ${palette.num} tabular-nums`}>{value}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{helper}</div>
        </Tag>
    );
}


function TabButton({ active, onClick, count, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
        >
            {label} <span className="ml-1 text-[11px] text-muted-foreground/80 font-normal">({count})</span>
        </button>
    );
}


function StatusBadge({ status }) {
    // iter102.25 — Canonical label ("In Production") + tone. Same
    // helper used across doctor, planner, admin, PES detail. Zero drift.
    const tone = statusToneClass(status) || "bg-slate-100 text-slate-700 border-slate-200";
    const label = statusLabel(status, "doctor");
    return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${tone}`}>
            {label}
        </span>
    );
}


function Avatar({ name, image }) {
    const initials = (name || "?")
        .split(/\s+/).filter(Boolean).slice(0, 2)
        .map((w) => w[0]?.toUpperCase()).join("") || "?";
    if (image) {
        return <img src={image} alt={name || ""} className="w-9 h-9 rounded-full object-cover ring-1 ring-border" />;
    }
    return (
        <div
            className="w-9 h-9 rounded-full grid place-items-center ring-1 ring-border text-white font-display text-xs font-bold"
            style={{ background: "linear-gradient(135deg, hsl(180 50% 45%), hsl(200 60% 50%))" }}
        >
            {initials}
        </div>
    );
}


// Re-export so any future cross-portal link can use the same empty state.
export function EmptyState({ title, helper }) {
    return (
        <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 grid place-items-center mb-3">
                <Sparkle size={24} className="text-primary" weight="duotone" />
            </div>
            <div className="font-display text-base font-semibold">{title}</div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto leading-relaxed">{helper}</p>
        </div>
    );
}
